import React from 'react';
import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { FielderHard, FielderPink } from '@core/theme/baseTheme';
const ACTIVE = '#dc5c87'; // do not change – identical to TaskTypesNav
const Frame = styled(Paper)(({ theme }) => ({
	width: '100%',
	background: 'transparent',
	borderRadius: 0,
	borderBottom: `1px solid ${FielderPink}`,
}));
const StyledTabs = styled((props) => (
	<Tabs
		{...props}
		slotProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
	/>
))(({ theme }) => ({
	minHeight: 46,
	'& .MuiTabs-indicator': {
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		bottom: 0,
	},
	'& .MuiTabs-indicatorSpan': {
		maxWidth: '100%',
		width: '100%',
		height: 3,
		borderRadius: 0,
		backgroundColor: ACTIVE,
	},
}));
const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
	({ theme }) => ({
		minHeight: 46,
		textTransform: 'none',
		fontWeight: 700,
		fontSize: 14,
		lineHeight: 1.15,
		padding: theme.spacing(1.5, 2),
		color: theme.palette.text.secondary,
		transition: 'all 140ms ease',
		'&:hover': {
			borderBottom: `1px solid ${FielderHard}`,
			borderTopLeftRadius: 10,
			borderTopRightRadius: 10,
			color: ACTIVE,
			backgroundColor:
				theme.palette.mode === 'dark'
					? alpha(ACTIVE, 0.12)
					: alpha(ACTIVE, 0.08),
		},
		'&.Mui-selected': {
			color: ACTIVE,
			backgroundColor:
				theme.palette.mode === 'dark'
					? alpha(ACTIVE, 0.18)
					: alpha(ACTIVE, 0.12),
			borderBottom: `1px solid ${FielderHard}`,
			borderTopLeftRadius: 10,
			borderTopRightRadius: 10,
		},
		'& .MuiTab-wrapper': {
			whiteSpace: 'pre-line',
			rowGap: 2,
		},
		'& .MuiTouchRipple-root': { display: 'none' },
	})
);
export const FielderSmartTabs = (
	{
		value,
		onChange,
		children,
		variant = 'fullWidth',
		centered = false,
		sx,
		...rest
	}) => (
	<Box sx={{ width: '100%', ...sx }}>
		<Frame elevation={0}>
			<StyledTabs
				value={value}
				onChange={onChange}
				variant={variant}
				centered={centered}
				{...rest}
			>
				{children}
			</StyledTabs>
		</Frame>
	</Box>
);
export const FielderSmartTab = (props) => <StyledTab {...props} />;
export const FielderSmartNav = (
	{
	 items,
	 value,
	 onChange,
	 centered = false,
	 variant = 'fullWidth',
	 sx,
	 ...rest
 }) => (
	<FielderSmartTabs
		value={value}
		onChange={(_, v) => onChange?.(v)}
		centered={centered}
		variant={variant}
		sx={sx}
		{...rest}
	>
		{items?.map((t) => (
			<FielderSmartTab
				key={t.id}
				value={t.id}
				label={t.label}
				disabled={!!t.disabled}
				icon={t.icon}
				iconPosition={t.icon ? 'start' : undefined}
			/>
		))}
	</FielderSmartTabs>
);
FielderSmartNav.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			label: PropTypes.node.isRequired,
			disabled: PropTypes.bool,
			icon: PropTypes.node,
		})
	),
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func,
	centered: PropTypes.bool,
	variant: PropTypes.oneOf(['fullWidth', 'standard', 'scrollable']),
	sx: PropTypes.object,
};
